/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 메인페이지의 학교 상세 하단 기본정보 탭
 * 버전 : 1.0
 * 파일명 : SchoolListDetailTab00.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================
 * 수정 : 2025-12-26, 강동형 : 대학교 취업률, 축제예정일 추가
 * 수정 : 2025-12-29, 강동형 : 메모 UI 추가
 * 수정 : 2026-01-27, 강동형 : SchoolMap에서 보여주는 데이터와 UI를 일치화
 * 수정 : 2026-02-02, 강동형 : 메모 업로드 기능 API 연결, 취업률 API 연결
 *================================================================*/

import { useState } from 'react'
import { BottomSheetType } from 'src/types/SchoolType'
import { parseDateToKorean } from 'src/utils/DateUtils'
import styled from 'styled-components'
import { MintButton } from '../buttons/CommonButtons'
import { AppImage } from 'src/assets/images/AppImage'
import { useModalStore } from 'src/stores/ModalStore'
import { Common_ModalFooter_SimpleDouble, Common_ModalTitle_Simple } from '../modals/CommonModal'
import { Dialog_ModalContent_TextArea } from '../modals/DialogModal'
import { useMainPageDialogStore } from 'src/stores/DialogStore'
import { postSchoolMemo } from 'src/api/SchoolService'
import { toast } from 'react-toastify'

interface Props{
    school : BottomSheetType
}
const ContentWrapper  = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0px;
`
const ContentContainer = styled.div`
    display: flex;
    align-items: center;
`
const HeaderWrapper = styled.div`
    width: 100px;
    font-weight:500;
    color: #000014;
    font-size: 14px;
    opacity: 0.5;
`

const GenderWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 100px;
    border: 1px solid #6FD3FF;
    background-color: #D6EFFF;
    padding: 4px 8px;
    color: #000014;
    font-size: 14px;
    font-weight: 500;
    max-height: 25px;
`

const ContentBox = styled.div`
    color: #000014;
    font-size: 14px;
    font-weight: 500;
`

const TextAreaField = styled.textarea`
    height: 60px;
    pointer-events: none;
`

const SchoolListDetailTab00 = ({school}:Props) => {
    const establish_Date = parseDateToKorean(school.establish_date)
    const [textAreaState,setTextAreaState] = useState(school.memo)
    const modalSetField = useModalStore(s=>s.setField)
    const reset = useMainPageDialogStore(s=>s.reset)

    let parseType = school.school_type
    if (school.school_type === '특성화고등학교') {
    parseType = '특성화고'
    } else if (school.school_type === '특수목적고등학교') {
    parseType = '특목고'
    } else if (school.school_type === '자율고등학교') {
    parseType = '자율고'
    }

    const remarks = [
    ...school.special_major_list,
    ...(school.rotc ? ['학군단'] : []),
    ...(school.military_agreement ? ['군협약'] : [])
    ].join(', ');

    const onClickCanclemMemo = ()=>{
        reset('tempTextArea')
        modalSetField('open',false)
    }

    const onClickConfirmMemo = async ()=>{
        const { tempTextArea } = useMainPageDialogStore.getState(); // ✅ 최신값
        try{
            await postSchoolMemo({school_id:school.school_id,school_class:school.school_class,content:tempTextArea})
            setTextAreaState(tempTextArea)
            toast.success('등록 성공')
            modalSetField('open',false)
        }
        catch{
            toast.error('등록 실패')
        }
    }

    const onClickMemo = ()=>{
        modalSetField('type','CENTER')
        modalSetField('width','calc(100vw - 20px)')
        modalSetField('padding','24px')
        modalSetField('title',<Common_ModalTitle_Simple title='메모 작성' customStyle={{justifyContent:'center',width:'100%',display:'flex', marginBottom:'16px'}}/>)
        modalSetField('content',<Dialog_ModalContent_TextArea textArea={textAreaState}/>)
        modalSetField('footer',
            <Common_ModalFooter_SimpleDouble 
                customCancleStyle={{backgroundColor:'#E3E3E3'}} 
                cancleAction={onClickCanclemMemo} 
                customConfirmStyle={{flex:1,width:'auto',boxShadow:'none'}}
                confirmText='작성 완료'
                confirmAction={onClickConfirmMemo}/>)
        modalSetField('open',true)
    }

    return (
        <ContentWrapper>
            <ContentContainer>
                <HeaderWrapper>설립 연도</HeaderWrapper>
                <ContentBox>{establish_Date || '정보없음'}</ContentBox>
            </ContentContainer>
            {parseType &&
            <ContentContainer>
                <HeaderWrapper>학교 종류</HeaderWrapper>
                <ContentBox>{parseType}</ContentBox>
            </ContentContainer>
            }
            {school?.class_cnt != null && school.class_cnt !== 0 && 
            <ContentContainer>
                <HeaderWrapper>학급 수</HeaderWrapper>
                <ContentBox>{school.class_cnt}</ContentBox>
            </ContentContainer>
            }
            <ContentContainer>
                <HeaderWrapper>학생 수</HeaderWrapper>
                <ContentBox>{school.total_student_cnt !== 0 ? 
                    `${school.total_student_cnt} 명`
                : 
                    '정보없음'}
                </ContentBox>
            </ContentContainer>
            <ContentContainer>
                <HeaderWrapper>성비</HeaderWrapper>
                <ContentBox style={{display:'flex'}}>
                    {school.total_male_rate ? 
                    <>
                    <GenderWrapper>♂ 남 {Math.round(school.total_male_rate)}</GenderWrapper>&nbsp;<span style={{padding:'4px'}}>:</span>&nbsp;<GenderWrapper style={{backgroundColor:'#DFF6DC', border:'1px solid #6BEA8C'}}>♀ 여 {Math.round(Math.abs(100 - school.total_male_rate))}</GenderWrapper>
                    </>
                    : 
                    '정보없음'}
                </ContentBox>
            </ContentContainer>
            {!['특성화고', '특목고', '자율고','일반고등학교'].includes(parseType) && 
            <>
                <ContentContainer>
                    <HeaderWrapper>취업률</HeaderWrapper>
                    <ContentBox>{school.employment_rate}%</ContentBox>
                </ContentContainer>
                <ContentContainer>
                    <HeaderWrapper>축제예정</HeaderWrapper>
                    <ContentBox>{school.festival_info}</ContentBox>
                </ContentContainer>
            </>
            }
            <ContentContainer>
                <HeaderWrapper>특이사항</HeaderWrapper>
                <ContentBox>{remarks || '정보없음'}</ContentBox>
            </ContentContainer>
            <ContentContainer style={{flexDirection:'column', alignItems:'flex-start', width:'100%', gap:'8px'}}>
                <HeaderWrapper>메모</HeaderWrapper>
                <TextAreaField value={textAreaState}/>
            </ContentContainer>
            <MintButton style={{marginTop:'-6px'}} onClick={onClickMemo}>
                <AppImage name='IconNotePadMint'/>
                메모 작성하기
            </MintButton>
        </ContentWrapper>
    )
}

export default SchoolListDetailTab00